import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

// Per-brand chat gating, owned by mono (single source across all surfaces).
// Fetched from GET /api/v5/decagon/config, which is public + edge-cached, so a
// traffic spike collapses at CloudFront rather than hitting mono per visitor.
export interface ChatConfig {
  chat_ab_test: boolean
  decagon: boolean
  zendesk: boolean
}

// Fail open to the existing Zendesk chat if mono is unreachable — never a broken
// or no-chat state because of a fetch error.
const FAIL_OPEN: ChatConfig = { chat_ab_test: false, decagon: false, zendesk: true }

let config: ChatConfig | null = null
let started = false
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((listener) => listener())
}

// Fetch once per page load (module state resets on full reload; mono's short
// edge TTL governs how fast a flag flip propagates). No client-side TTL needed.
export function ensureChatConfig(): void {
  if (started || typeof window === 'undefined' || typeof fetch !== 'function') return
  started = true
  fetch(getV5ApiUrl(`/decagon/config?brand_id=${encodeURIComponent(brand.key)}`), {
    headers: { Accept: 'application/json' },
  })
    .then((res) =>
      res.ok ? res.json() : Promise.reject(new Error(`decagon config ${res.status}`)),
    )
    .then((c: ChatConfig) => {
      config = {
        chat_ab_test: Boolean(c?.chat_ab_test),
        decagon: Boolean(c?.decagon),
        zendesk: Boolean(c?.zendesk),
      }
    })
    .catch(() => {
      config = FAIL_OPEN
    })
    .finally(notify)
}

export function getChatConfig(): ChatConfig | null {
  return config
}

export function subscribeChatConfig(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
